// ProfileLayout — the account hub frame. The profile is an entity surface
// where the entity is the user: an identity header + a URL-driven tab row + the
// active tab's <Outlet>. Tabs are NAVIGATION (deep-linkable sub-routes on NavLink
// variant="tabs" — the same visual system as the entity/top nav; a tab switch
// REPLACES the history entry, the variant's default — ADR-037 §2), never the
// in-page Tabs primitive. APP-143 grows this by adding tabs (Security · Sessions ·
// Notifications · Scheduled reports) as sibling routes — no reframe.
//
// Authed project-less page (ANATOMY §Three traversal patterns). The Tokens tab is
// withheld from sysadmins — they never mint PATs (ADR-004; platform automation uses
// an application key).

import { Outlet } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useMe } from '../../hooks/useMe.js';
import { useDocumentTitle } from '../../hooks/useDocumentTitle.js';
import { NavLink } from '../../components/ui/NavLink.js';
import { HScroll } from '../../components/ui/HScroll.js';

export function ProfileLayout() {
  const { t } = useTranslation('auth');
  const { data: me } = useMe();
  useDocumentTitle(t('profile.title'));
  if (!me) return null;

  const isSysadmin = me.user.is_sysadmin;

  return (
    <div className="max-w-4xl mx-auto px-6 py-8">
      <header className="mb-4">
        <h1 className="text-xl font-bold text-[var(--color-text-primary)]">
          {me.user.first_name} {me.user.last_name}
        </h1>
        <p className="text-sm text-[var(--color-text-secondary)] font-mono">{me.user.username}</p>
      </header>

      <HScroll className="border-b border-[var(--color-border)] mb-6">
        <nav className="flex gap-1" aria-label={t('profile.tabsLabel')}>
          <NavLink to="/profile" end variant="tabs">
            {t('profile.tabs.profile')}
          </NavLink>
          {!isSysadmin && (
            <NavLink to="/profile/tokens" variant="tabs">
              {t('profile.tabs.tokens')}
            </NavLink>
          )}
        </nav>
      </HScroll>

      <Outlet />
    </div>
  );
}

export default ProfileLayout;
